/* Predictive search: the live suggestions under every search field.
 *
 * Loaded from layout/theme.liquid rather than from a section's own
 * `javascript` block, because there are two fields on two different
 * schedules now -- the search template's (sections/search.liquid) and the
 * header's dropdown (sections/header.liquid), which exists on every page.
 * A section-scoped copy would only ever run on one of them.
 *
 * The panel's markup is rendered by sections/predictive-search.liquid and
 * fetched with ?section_id=, rather than built here from
 * /search/suggest.json: money formatting, translations and image sizing
 * already exist in Liquid, and a JSON panel would have to re-implement all
 * three and then drift from them.
 *
 * Scoped to `[data-predictive]` (see snippets/search-form.liquid), so
 * dropping another field anywhere in the theme needs nothing from this file.
 */

var MIN_CHARS = 2;
var DEBOUNCE = 200;

function initPredictiveSearch(root) {
  var input = root.querySelector('[data-predictive-input]');
  var panel = root.querySelector('[data-predictive-panel]');
  var status = root.querySelector('[data-predictive-status]');
  var endpoint = root.dataset.predictiveUrl;
  if (!input || !panel || !endpoint) return;
  // The header renders on every page, so re-running this file (or a section
  // re-render in the Customizer) must not stack a second set of listeners.
  if (root.dataset.predictiveReady === 'true') return;
  root.dataset.predictiveReady = 'true';

  var timer = null;
  var controller = null;
  var options = [];
  var active = -1;

  var setActive = function (index) {
    if (options[active]) options[active].classList.remove('is-active');
    active = index;
    var option = options[active];
    if (!option) {
      input.removeAttribute('aria-activedescendant');
      return;
    }
    option.classList.add('is-active');
    input.setAttribute('aria-activedescendant', option.id);
    option.scrollIntoView({ block: 'nearest' });
  };

  var close = function () {
    panel.hidden = true;
    panel.innerHTML = '';
    input.setAttribute('aria-expanded', 'false');
    input.removeAttribute('aria-activedescendant');
    options = [];
    active = -1;
  };

  var open = function (html) {
    panel.innerHTML = html;
    panel.hidden = false;
    input.setAttribute('aria-expanded', 'true');
    options = [].slice.call(panel.querySelectorAll('[role="option"]'));
    active = -1;

    // The panel is a visual event; a screen reader gets the count instead.
    if (status) {
      status.textContent = options.length ? options.length + ' suggestions' : 'No suggestions';
    }

    options.forEach(function (option, i) {
      option.addEventListener('mouseenter', function () {
        setActive(i);
      });
      option.addEventListener('click', function () {
        window.location.href = option.dataset.url;
      });
    });
  };

  var search = function (terms) {
    // Abort the previous request rather than let it land: a slow response to
    // "so" arriving after a fast one to "social" would replace the panel with
    // results for what was typed three letters ago.
    if (controller) controller.abort();
    controller = new AbortController();

    /* The trailing wildcard is the difference between typing and having to
       finish the word. Shopify's predictive endpoint matches whole indexed
       terms, inconsistently on partials -- "soci" finds Social, "socia" finds
       nothing, and "unwin" never finds Unwind -- which reads as the
       suggestions being broken. It prefix-matches the last term of the query
       when that term ends in `*`, so every keystroke gets appended one:
       "unwin*" -> Unwind. Left off the earlier terms, which is Shopify's own
       rule, not a choice here -- only the last one accepts it. */
    var url =
      endpoint +
      '?q=' +
      encodeURIComponent(terms + '*') +
      '&section_id=predictive-search' +
      '&resources[type]=product,article,page' +
      '&resources[limit]=5';

    fetch(url, { signal: controller.signal })
      .then(function (response) {
        if (!response.ok) throw new Error(response.status);
        return response.text();
      })
      .then(function (text) {
        // The response is the section wrapped in its own div; the panel wants
        // what is inside it.
        var doc = new DOMParser().parseFromString(text, 'text/html');
        var section = doc.getElementById('shopify-section-predictive-search');
        var html = section ? section.innerHTML.trim() : '';
        if (html) {
          open(html);
        } else {
          close();
        }
      })
      .catch(function (error) {
        // An abort is the normal path here, not a failure.
        if (error.name !== 'AbortError') close();
      });
  };

  input.addEventListener('input', function () {
    var terms = input.value.trim();
    clearTimeout(timer);

    if (terms.length < MIN_CHARS) {
      if (controller) controller.abort();
      close();
      return;
    }

    timer = setTimeout(function () {
      search(terms);
    }, DEBOUNCE);
  });

  input.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') {
      close();
      return;
    }
    if (panel.hidden || !options.length) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive((active + 1) % options.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive(active <= 0 ? options.length - 1 : active - 1);
    } else if (event.key === 'Enter' && options[active]) {
      // Only when a row is selected -- otherwise Enter is the form's, and
      // submitting to the full results page is the right answer.
      event.preventDefault();
      window.location.href = options[active].dataset.url;
    }
  });

  document.addEventListener('click', function (event) {
    if (!root.contains(event.target)) close();
  });

  /* Enter (or the Search button) gets the same wildcard the panel above
     sends, so the results page finds what the panel just offered: without it
     "unwin" suggests Unwind and then lands on a page that says there is no
     such thing. Rewritten as a navigation rather than by editing the field,
     which would flash an asterisk into what the shopper typed; the page
     strips it back off for display (sections/search.liquid).

     The form still submits on its own with JavaScript off — that path just
     searches without the wildcard, which is what it has always done. */
  var form = input.closest('form');
  if (form) {
    form.addEventListener('submit', function (event) {
      var terms = input.value.trim();
      if (!terms || terms.slice(-1) === '*') return;
      event.preventDefault();
      window.location.href =
        form.getAttribute('action') + '?q=' + encodeURIComponent(terms + '*');
    });
  }

  input.addEventListener('focus', function () {
    if (input.value.trim().length >= MIN_CHARS && panel.innerHTML) {
      panel.hidden = false;
      input.setAttribute('aria-expanded', 'true');
    }
  });
}

function initAll() {
  document.querySelectorAll('[data-predictive]').forEach(initPredictiveSearch);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAll);
} else {
  initAll();
}

// A section re-render in the Customizer replaces the markup, and with it the
// element every listener above was bound to.
document.addEventListener('shopify:section:load', initAll);
